import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

export const SUPPORTED_LANGUAGES = [
    "en",
    "fr",
    "cs",
    "de",
    "es",
    "pt",
    "it",
    "pl",
    "sk",
    "hu",
    "ro",
];

export const DEFAULT_REGIONAL_PREFERENCES = {
    language: "en",
    timezone: "UTC",
    terminology: {
        field: "garden",
    },
    units: {
        temperature: "celsius",
        distance: "metric",
        area: "hectare",
        rainfall: "mm",
        wind: "kmh",
        yield: "t_ha",
        weight: "metric",
        waterdepth: "mm",
        watervolume: "l",
        soilmoisture: "m3_m3",
        radiation: "kwh_m2",
    },
    format: {
        date: "yyyy-MM-dd",
        time: "24h",
        firstDayOfWeek: "monday",
    },
};

const RegionalContext = createContext(null);

export function getSupportedLanguage(language) {
    return SUPPORTED_LANGUAGES.includes(language) ? language : "en";
}

export function getBrowserLanguage() {
    if (typeof navigator === "undefined") return "en";

    const language = navigator.language || "en";

    if (language.startsWith("cs")) return "cs";
    if (language.startsWith("de")) return "de";
    if (language.startsWith("fr")) return "fr";
    if (language.startsWith("es")) return "es";
    if (language.startsWith("pt")) return "pt";
    if (language.startsWith("it")) return "it";
    if (language.startsWith("pl")) return "pl";
    if (language.startsWith("sk")) return "sk";
    if (language.startsWith("hu")) return "hu";
    if (language.startsWith("ro")) return "ro";

    return "en";
}

function parseMaybeJson(value) {
    if (!value) return {};

    if (typeof value === "object") {
        return value;
    }

    if (typeof value === "string") {
        try {
            return JSON.parse(value);
        } catch {
            return {};
        }
    }

    return {};
}

export function getRegionalPreferencesFromUser(user) {
    const raw =
        user?.regional_preferences ||
        user?.regionalPreferences ||
        user?.preferences ||
        {};

    const parsed = parseMaybeJson(raw);

    return parsed?.preferences || parsed || {};
}

export function mergeRegionalPreferences(preferences = {}) {
    return {
        ...DEFAULT_REGIONAL_PREFERENCES,
        ...preferences,

        language: getSupportedLanguage(
            preferences.language || DEFAULT_REGIONAL_PREFERENCES.language
        ),

        terminology: {
            ...DEFAULT_REGIONAL_PREFERENCES.terminology,
            ...(preferences.terminology || {}),
        },

        units: {
            ...DEFAULT_REGIONAL_PREFERENCES.units,
            ...(preferences.units || {}),
        },

        format: {
            ...DEFAULT_REGIONAL_PREFERENCES.format,
            ...(preferences.format || {}),
        },
    };
}

function buildPreferencesFromUser(user) {
    const preferences = getRegionalPreferencesFromUser(user);

    return mergeRegionalPreferences({
        ...preferences,
        language: preferences.language || user?.language || getBrowserLanguage(),
    });
}

function round(value, digits = 1) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
    }

    return Number(value).toLocaleString(undefined, {
        maximumFractionDigits: digits,
    });
}

function capitalize(value) {
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function normalizeUnit(value) {
    return String(value || "").toLowerCase();
}

function convertTemperature(value, unit) {
    if (unit === "fahrenheit") return value * (9 / 5) + 32;
    return value;
}

function convertTemperatureDelta(value, unit) {
    if (unit === "fahrenheit") return value * (9 / 5);
    return value;
}

function convertSapFlow(value, unitSystem) {
    if (unitSystem === "imperial") return value / 28.3495;
    return value;
}

function convertPlantDiameter(value, distanceSystem) {
    if (distanceSystem === "imperial") return value / 25.4;
    return value;
}

function convertRainfall(value, unit) {
    if (unit === "in") {
        return value / 25.4;
    }

    return value;
}

function convertWaterDepth(value, unit) {
    if (unit === "in") {
        return value / 25.4;
    }

    return value;
}

function convertWind(value, unit) {
    if (unit === "mph") {
        return value * 0.621371;
    }

    return value;
}

function convertArea(value, unit) {
    if (unit === "acre") {
        return value * 2.47105;
    }

    return value;
}

function convertYield(value, unit) {
    if (unit === "lb_acre") {
        return value * 892.179;
    }

    return value;
}

function convertWeight(value, sourceUnit, unitSystem) {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
        return {
            value: numericValue,
            suffix: sourceUnit || "",
        };
    }

    const normalizedSourceUnit = normalizeUnit(sourceUnit || "kg");

    const grams =
        normalizedSourceUnit === "g"
            ? numericValue
            : normalizedSourceUnit === "kg"
              ? numericValue * 1000
              : numericValue;

    if (unitSystem === "imperial") {
        const ounces = grams / 28.3495;

        if (Math.abs(ounces) < 16) {
            return {
                value: ounces,
                suffix: "oz",
            };
        }

        return {
            value: ounces / 16,
            suffix: "lb",
        };
    }

    if (normalizedSourceUnit === "g") {
        return {
            value: grams,
            suffix: "g",
        };
    }

    return {
        value: grams / 1000,
        suffix: "kg",
    };
}

function convertWaterVolume(value, unit) {
    if (unit === "gallon") return value / 3.78541;
    return value;
}

function convertSoilMoistureValue(value, unit) {
    if (unit === "percent") return value * 100;
    return value;
}

function convertRadiation(value, unit) {
    if (unit === "kwh_m2") {
        return value / 3.6;
    }

    if (unit === "btu_ft2") {
        return value * 88.055;
    }

    return value;
}

function normalizeLiters(value, sourceUnit = "l") {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
        return null;
    }

    const normalizedSourceUnit = normalizeUnit(sourceUnit);

    if (
        normalizedSourceUnit === "gal" ||
        normalizedSourceUnit === "gallon" ||
        normalizedSourceUnit === "gallons"
    ) {
        return numericValue * 3.78541;
    }

    return numericValue;
}

function formatDateParts(date, timezone) {
    const safeDate = date instanceof Date ? date : new Date(date);

    if (Number.isNaN(safeDate.getTime())) {
        return null;
    }

    try {
        const parts = new Intl.DateTimeFormat("en-CA", {
            timeZone: timezone || "UTC",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).formatToParts(safeDate);

        return {
            year: parts.find((part) => part.type === "year")?.value,
            month: parts.find((part) => part.type === "month")?.value,
            day: parts.find((part) => part.type === "day")?.value,
        };
    } catch {
        return formatDateParts(safeDate, "UTC");
    }
}

function formatDateValue(date, preferences) {
    const parts = formatDateParts(date, preferences.timezone);

    if (!parts) return "—";

    const dateFormat = preferences.format.date;

    if (dateFormat === "MM/dd/yyyy") {
        return `${parts.month}/${parts.day}/${parts.year}`;
    }

    if (dateFormat === "dd/MM/yyyy") {
        return `${parts.day}/${parts.month}/${parts.year}`;
    }

    return `${parts.year}-${parts.month}-${parts.day}`;
}

function formatTimeValue(date, preferences) {
    const safeDate = date instanceof Date ? date : new Date(date);

    if (Number.isNaN(safeDate.getTime())) {
        return "—";
    }

    try {
        return new Intl.DateTimeFormat(undefined, {
            timeZone: preferences.timezone || "UTC",
            hour: "numeric",
            minute: "2-digit",
            hour12: preferences.format.time === "12h",
        }).format(safeDate);
    } catch {
        return new Intl.DateTimeFormat(undefined, {
            timeZone: "UTC",
            hour: "numeric",
            minute: "2-digit",
            hour12: preferences.format.time === "12h",
        }).format(safeDate);
    }
}

export function RegionalProvider({ user, children }) {
    const userPreferences = useMemo(() => {
        return buildPreferencesFromUser(user);
    }, [user]);

    const [regionalPreferences, setRegionalPreferencesState] =
        useState(userPreferences);

    useEffect(() => {
        setRegionalPreferencesState(userPreferences);
    }, [userPreferences]);

    const setRegionalPreferences = useCallback((nextPreferences) => {
        setRegionalPreferencesState((currentPreferences) => {
            const resolved =
                typeof nextPreferences === "function"
                    ? nextPreferences(currentPreferences)
                    : nextPreferences;

            return mergeRegionalPreferences({
                ...currentPreferences,
                ...(resolved || {}),
                terminology: {
                    ...currentPreferences.terminology,
                    ...(resolved?.terminology || {}),
                },
                units: {
                    ...currentPreferences.units,
                    ...(resolved?.units || {}),
                },
                format: {
                    ...currentPreferences.format,
                    ...(resolved?.format || {}),
                },
            });
        });
    }, []);

    const setLanguage = useCallback(
        (language) => {
            setRegionalPreferences({
                language: getSupportedLanguage(language),
            });
        },
        [setRegionalPreferences]
    );

    const value = useMemo(() => {
        const preferences = regionalPreferences;

        const waterDepthUnit = preferences.units.waterdepth;
        const waterVolumeUnit = preferences.units.watervolume;
        const soilMoistureUnit = preferences.units.soilmoisture;
        const temperatureUnit = preferences.units.temperature;
        const weightUnit = preferences.units.weight;
        const distanceUnit = preferences.units.distance;
        const waterDepthUnitLabel = waterDepthUnit === "in" ? "in" : "mm";
        const waterVolumeUnitLabel = waterVolumeUnit === "gallon" ? "gal" : "L";
        const soilMoistureUnitLabel = soilMoistureUnit === "percent" ? "%" : "m³/m³";
        const temperatureUnitLabel = temperatureUnit === "fahrenheit" ? "°F" : "°C";
        const sapFlowUnitLabel = weightUnit === "imperial" ? "oz/h" : "g/h";
        const plantDiameterUnitLabel = distanceUnit === "imperial" ? "in" : "mm";

        return {
            regionalPreferences: preferences,
            setRegionalPreferences,
            setLanguage,

            terminology: preferences.terminology,
            units: preferences.units,
            format: preferences.format,
            timezone: preferences.timezone,
            language: preferences.language,

            fieldTerm: preferences.terminology.field,
            fieldTermLabel: capitalize(preferences.terminology.field),

            waterDepthUnit,
            waterVolumeUnit,
            soilMoistureUnit,
            temperatureUnit,
            waterDepthUnitLabel,
            waterVolumeUnitLabel,
            soilMoistureUnitLabel,
            temperatureUnitLabel,
            sapFlowUnitLabel,
            plantDiameterUnitLabel,

            formatTemperature(value, digits = 1) {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return "—";
                return `${round(convertTemperature(numericValue, temperatureUnit), digits)} ${temperatureUnitLabel}`;
            },

            convertTemperature(value) {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return 0;
                return convertTemperature(numericValue, temperatureUnit);
            },

            formatTemperatureDelta(value, digits = 1) {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return "—";
                return `${round(convertTemperatureDelta(numericValue, temperatureUnit), digits)} ${temperatureUnitLabel}`;
            },

            convertTemperatureDelta(value) {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return 0;
                return convertTemperatureDelta(numericValue, temperatureUnit);
            },

            formatSapFlow(value, digits = 1) {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return "—";
                return `${round(convertSapFlow(numericValue, weightUnit), digits)} ${sapFlowUnitLabel}`;
            },

            convertSapFlow(value) {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return 0;
                return convertSapFlow(numericValue, weightUnit);
            },

            formatPlantDiameter(value, digits = 2) {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return "—";
                return `${round(convertPlantDiameter(numericValue, distanceUnit), digits)} ${plantDiameterUnitLabel}`;
            },

            convertPlantDiameter(value) {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return 0;
                return convertPlantDiameter(numericValue, distanceUnit);
            },

            formatRainfall(value, digits = 1) {
                const numericValue = Number(value);
                const unit = preferences.units.rainfall;
                const converted = convertRainfall(numericValue, unit);
                const suffix = unit === "in" ? "in" : "mm";

                return `${round(converted, digits)} ${suffix}`;
            },

            convertRainfall(value) {
                const numericValue = Number(value);

                if (!Number.isFinite(numericValue)) {
                    return 0;
                }

                return convertRainfall(
                    numericValue,
                    preferences.units.rainfall
                );
            },

            rainfallUnitLabel:
                preferences.units.rainfall === "in" ? "in" : "mm",

            formatWaterDepth(value, digits = 1) {
                const numericValue = Number(value);

                if (!Number.isFinite(numericValue)) {
                    return "—";
                }

                const converted = convertWaterDepth(
                    numericValue,
                    waterDepthUnit
                );

                return `${round(converted, digits)} ${waterDepthUnitLabel}`;
            },

            convertWaterDepth(value) {
                const numericValue = Number(value);

                if (!Number.isFinite(numericValue)) {
                    return 0;
                }

                return convertWaterDepth(
                    numericValue,
                    waterDepthUnit
                );
            },

            formatWaterVolume(
                value,
                sourceUnit = "l",
                digits = 1,
                compact = false
            ) {
                const liters = normalizeLiters(value, sourceUnit);

                if (liters === null) {
                    return "—";
                }

                const converted = convertWaterVolume(
                    liters,
                    waterVolumeUnit
                );

                if (compact) {
                    if (Math.abs(converted) >= 1000000) {
                        return `${round(
                            converted / 1000000,
                            digits
                        )}M ${waterVolumeUnitLabel}`;
                    }

                    if (Math.abs(converted) >= 1000) {
                        return `${round(
                            converted / 1000,
                            0
                        )}K ${waterVolumeUnitLabel}`;
                    }
                }

                return `${round(converted, digits)} ${waterVolumeUnitLabel}`;
            },

            convertWaterVolume(value, sourceUnit = "l") {
                const liters = normalizeLiters(value, sourceUnit);
                if (liters === null) return 0;
                return convertWaterVolume(liters, waterVolumeUnit);
            },

            formatSoilMoisture(value, digits = soilMoistureUnit === "percent" ? 1 : 3) {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return "—";
                const converted = convertSoilMoistureValue(numericValue, soilMoistureUnit);
                return `${round(converted, digits)} ${soilMoistureUnitLabel}`;
            },

            convertSoilMoisture(value) {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return 0;
                return convertSoilMoistureValue(numericValue, soilMoistureUnit);
            },

            formatWind(value, digits = 1) {
                const numericValue = Number(value);
                const unit = preferences.units.wind;
                const converted = convertWind(numericValue, unit);
                const suffix = unit === "mph" ? "mph" : "km/h";

                return `${round(converted, digits)} ${suffix}`;
            },

            formatArea(value, digits = 1) {
                const numericValue = Number(value);
                const unit = preferences.units.area;
                const converted = convertArea(numericValue, unit);
                const suffix = unit === "acre" ? "ac" : "ha";

                return `${round(converted, digits)} ${suffix}`;
            },

            formatYield(value, digits = 1) {
                const numericValue = Number(value);
                const unit = preferences.units.yield;
                const converted = convertYield(numericValue, unit);
                const suffix = unit === "lb_acre" ? "lb/ac" : "t/ha";

                return `${round(converted, digits)} ${suffix}`;
            },

            formatWeight(value, sourceUnit = "kg", digits = 1) {
                const unit = preferences.units.weight;
                const converted = convertWeight(value, sourceUnit, unit);

                return `${round(converted.value, digits)} ${converted.suffix}`;
            },

            formatRadiation(value, digits = 1) {
                const numericValue = Number(value);

                if (!Number.isFinite(numericValue)) {
                    return "—";
                }

                const unit = preferences.units.radiation;
                const converted = convertRadiation(numericValue, unit);

                if (unit === "kwh_m2") {
                    return `${round(converted, digits)} kWh/m²`;
                }

                if (unit === "btu_ft2") {
                    return `${round(converted, digits)} BTU/ft²`;
                }

                return `${round(converted, digits)} MJ/m²`;
            },

            convertRadiation(value) {
                const numericValue = Number(value);

                if (!Number.isFinite(numericValue)) {
                    return 0;
                }

                return convertRadiation(
                    numericValue,
                    preferences.units.radiation
                );
            },

            radiationUnitLabel:
                preferences.units.radiation === "kwh_m2"
                    ? "kWh/m²"
                    : preferences.units.radiation === "btu_ft2"
                      ? "BTU/ft²"
                      : "MJ/m²",

            formatDate(date) {
                return formatDateValue(date, preferences);
            },

            formatTime(date) {
                return formatTimeValue(date, preferences);
            },

            formatDateTime(date) {
                return `${formatDateValue(date, preferences)} ${formatTimeValue(
                    date,
                    preferences
                )}`;
            },
        };
    }, [regionalPreferences, setRegionalPreferences, setLanguage]);

    return (
        <RegionalContext.Provider value={value}>
            {children}
        </RegionalContext.Provider>
    );
}

export function useRegional() {
    const context = useContext(RegionalContext);

    if (!context) {
        throw new Error("useRegional must be used inside RegionalProvider");
    }

    return context;
}